import {
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react'
import type { RefObject } from 'react'
import pointer, { isPath } from '../utils/json-pointer'
import type { FieldProps, Path } from '../types'
import DataContext from '../DataContext/Context'
import IterateItemContext from '../Iterate/IterateItemContext'

export type UseExternalValueProps<Value> = {
  path?: Path | undefined
  itemPath?: Path
  value?: Value
  getValueSnapshot?: (value: unknown) => unknown
  transformers?: RefObject<{
    fromExternal: FieldProps<Value>['fromExternal']
  }>
  emptyValue?: FieldProps<Value>['emptyValue']
}

export default function useExternalValue<Value>(
  props: UseExternalValueProps<Value>
) {
  const {
    path,
    itemPath,
    value = undefined,
    getValueSnapshot,
    transformers,
    emptyValue = undefined,
  } = props
  const dataContext = useContext(DataContext)
  const { data, subscribeDataValue, getDataValue } = dataContext || {}
  const iterateItemContext = useContext(IterateItemContext)
  const inIterate = Boolean(iterateItemContext)
  const { value: iterateElementValue } = iterateItemContext || {}
  const subscribablePath = isPath(path) ? path : undefined

  const subscribe = useCallback(
    (callback: () => void) => {
      if (subscribablePath && subscribeDataValue) {
        return subscribeDataValue(subscribablePath, callback)
      }

      return () => undefined
    },
    [subscribablePath, subscribeDataValue]
  )

  const getSnapshot = useCallback(() => {
    if (subscribablePath && getDataValue) {
      const value = getDataValue(subscribablePath)
      return getValueSnapshot ? getValueSnapshot(value) : value
    }

    return undefined
  }, [getDataValue, getValueSnapshot, subscribablePath])

  const dataValueSnapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  )

  const dataValue =
    getValueSnapshot && subscribablePath && getDataValue
      ? getDataValue(subscribablePath)
      : dataValueSnapshot

  return useMemo(() => {
    if (value !== undefined && value !== emptyValue) {
      // Value-prop sent directly to the field has highest priority, overriding any surrounding source
      return transformers?.current?.fromExternal?.(value) ?? emptyValue
    }

    if (
      subscribablePath &&
      getDataValue &&
      (!inIterate || !itemPath || dataValue !== undefined)
    ) {
      return (
        transformers?.current?.fromExternal?.(dataValue as Value) ??
        emptyValue
      )
    }

    if (inIterate && itemPath) {
      // This field is inside an iterate, and has a pointer from the base of the element being iterated
      if (itemPath === '/') {
        return (
          transformers?.current?.fromExternal?.(
            iterateElementValue as Value
          ) ?? emptyValue
        )
      }

      if (pointer.has(iterateElementValue, itemPath)) {
        return (
          transformers?.current?.fromExternal?.(
            pointer.get(iterateElementValue, itemPath) as Value
          ) ?? emptyValue
        )
      }
    }

    if (data && path) {
      // There is a surrounding data context and a path for where in the source to find the data
      if (path === '/') {
        return transformers?.current?.fromExternal?.(data) ?? emptyValue
      }

      if (pointer.has(data, path)) {
        return (
          transformers?.current?.fromExternal?.(pointer.get(data, path)) ??
          emptyValue
        )
      }
    }

    return emptyValue
  }, [
    data,
    dataValue,
    getDataValue,
    getValueSnapshot,
    emptyValue,
    inIterate,
    itemPath,
    iterateElementValue,
    path,
    subscribablePath,
    transformers,
    value,
  ])
}
